import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api, { posApi } from '@/services/api';
import { applyColumnMapping, autoMapHeaders } from './autoMap';
import { parseWorkbook } from './parseWorkbook';
import FileStep from './steps/FileStep';
import MappingStep from './steps/MappingStep';
import ReviewStep from './steps/ReviewStep';
import ValidateStep from './steps/ValidateStep';
import CompleteStep from './steps/CompleteStep';
import type {
  CityOption,
  EntityImportConfig,
  ImportError,
  ImportPreviewRow,
  ImportResult,
  ImportStep,
  ValidateResult,
} from './types';
import { importEntityDescription, importEntityTitle, importSkipReason } from './importI18n';
import {
  downloadErrorRowsCsv,
  downloadSkippedRowsCsv,
  errorRowNumbers,
  type SkippedImportRow,
} from './errorRowsExport';

const STEP_ORDER: ImportStep[] = ['file', 'mapping', 'review', 'validate', 'complete'];

interface EntityImportWizardProps {
  config: EntityImportConfig;
  onClose: () => void;
  onSuccess?: () => void;
}

function storageKey(config: EntityImportConfig): string {
  const company = localStorage.getItem('company_code') || 'default';
  return `${config.localStorageKey}:${company}`;
}

function loadSavedMapping(config: EntityImportConfig): Record<string, string> {
  try {
    const raw = localStorage.getItem(storageKey(config));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function saveMapping(config: EntityImportConfig, mapping: Record<string, string>) {
  try {
    localStorage.setItem(storageKey(config), JSON.stringify(mapping));
  } catch {
    // ignore quota errors
  }
}

export default function EntityImportWizard({ config, onClose, onSuccess }: EntityImportWizardProps) {
  const { t } = useTranslation();
  const stepLabel = (s: ImportStep) => t(`import.steps.${s}`);
  const [step, setStep] = useState<ImportStep>('file');
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [previewRows, setPreviewRows] = useState<ImportPreviewRow[]>([]);
  const [manualSkips, setManualSkips] = useState<Set<number>>(new Set());
  const [dragging, setDragging] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validateResult, setValidateResult] = useState<ValidateResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  /** Validation errors kept after a valid-only import so user can still download them. */
  const [heldValidationErrors, setHeldValidationErrors] = useState<ImportError[]>([]);

  const { data: cities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const res = await posApi.getCities();
      return res.data.message as CityOption[];
    },
  });

  const { data: uomData = [] } = useQuery({
    queryKey: ['uoms'],
    enabled: Boolean(config.showUomGuide),
    queryFn: async () => {
      const res = await posApi.getUOMs();
      return (res.data.message || []) as { name?: string; unit_of_measure?: string }[];
    },
  });

  const uomLabels = useMemo(
    () =>
      (uomData as unknown[])
        .map((u) => {
          if (typeof u === 'string') return u.trim();
          if (u && typeof u === 'object') {
            const row = u as { name?: string; unit_of_measure?: string };
            return String(row.unit_of_measure || row.name || '').trim();
          }
          return '';
        })
        .filter(Boolean),
    [uomData]
  );

  const stepIndex = STEP_ORDER.indexOf(step);

  const requiredMapped = config.fields
    .filter((f) => f.required)
    .every((f) => Boolean(mapping[f.fieldname]));

  const rebuildPreview = useCallback(
    (rows: Record<string, string>[], map: Record<string, string>, skips: Set<number>) => {
      const mapped = applyColumnMapping(rows, map).map((r) => config.normalizeRow(r));
      const seenMobiles = new Map<string, number>();
      const seenKeys = new Map<string, number>();
      const next: ImportPreviewRow[] = mapped.map((fields, idx) => {
        const rowNumber = idx + 2;
        if (skips.has(rowNumber)) {
          return {
            rowNumber,
            fields,
            status: 'skipped',
            skipReason: 'manual_skip',
            skipReasonLabel: importSkipReason(t, 'manual_skip', config),
          };
        }
        const issue = config.clientValidateRow(fields, { cities, seenMobiles, seenKeys, rowNumber });
        if (issue?.reason) {
          return {
            rowNumber,
            fields,
            status: 'skipped',
            skipReason: issue.reason,
            skipReasonLabel: issue.label || importSkipReason(t, issue.reason, config),
          };
        }
        return { rowNumber, fields, status: 'ready' };
      });
      setPreviewRows(next);
    },
    [cities, config]
  );

  useEffect(() => {
    if (rawRows.length && Object.keys(mapping).length) {
      rebuildPreview(rawRows, mapping, manualSkips);
    }
  }, [rawRows, mapping, manualSkips, rebuildPreview]);

  const handleFileSelected = async (selected: File) => {
    try {
      const parsed = await parseWorkbook(selected);
      setFile(selected);
      setFileName(selected.name);
      setHeaders(parsed.headers);
      setRawRows(parsed.rows);
      setValidateResult(null);
      setImportResult(null);
      setManualSkips(new Set());

      const saved = loadSavedMapping(config);
      const auto = autoMapHeaders(parsed.headers, config.fields, config.headerAliases);
      const merged: Record<string, string> = { ...auto };
      for (const [field, col] of Object.entries(saved)) {
        if (col && parsed.headers.includes(col)) merged[field] = col;
      }
      setMapping(merged);
      toast.success(`Loaded ${parsed.rows.length} row(s)`);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to parse file');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const res = await api.get(config.api.downloadTemplate, {
        params: { entity: config.entityId },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${config.doctypeLabel.replace(/\s+/g, '_')}_Import_Template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleMappingChange = (fieldname: string, column: string) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (!column) delete next[fieldname];
      else next[fieldname] = column;
      saveMapping(config, next);
      return next;
    });
    setValidateResult(null);
  };

  const handleToggleSkip = (rowNumber: number) => {
    setManualSkips((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
    setValidateResult(null);
  };

  const skippedRowNumbers = useMemo(() => {
    return previewRows.filter((r) => r.status === 'skipped').map((r) => r.rowNumber);
  }, [previewRows]);

  const skippedRowsForValidate = useMemo((): SkippedImportRow[] => {
    return previewRows
      .filter((r) => r.status === 'skipped')
      .map((r) => ({
        row: r.rowNumber,
        reason: r.skipReasonLabel || r.skipReason || 'Skipped',
      }))
      .sort((a, b) => a.row - b.row);
  }, [previewRows]);

  const validationErrorRows = useMemo(
    () => errorRowNumbers(validateResult?.errors),
    [validateResult],
  );

  const importErrorRows = useMemo(
    () => errorRowNumbers(importResult?.errors),
    [importResult],
  );

  /** Rows to skip on import: client skips + validation error rows (import valid-only). */
  const importSkipRowNumbers = useMemo(() => {
    const merged = new Set<number>(skippedRowNumbers);
    for (const row of validationErrorRows) merged.add(row);
    return [...merged].sort((a, b) => a - b);
  }, [skippedRowNumbers, validationErrorRows]);

  const buildFormData = (validateOnly: boolean, skipRows?: number[]) => {
    if (!file) throw new Error('No file selected');
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('entity', config.entityId);
    formData.append('validate_only', validateOnly ? 'true' : 'false');
    formData.append('custom_mapping', JSON.stringify(mapping));
    formData.append('skipped_rows', JSON.stringify(skipRows ?? skippedRowNumbers));
    return formData;
  };

  const resetWizard = () => {
    setStep('file');
    setFile(null);
    setFileName(null);
    setHeaders([]);
    setRawRows([]);
    setMapping({});
    setPreviewRows([]);
    setManualSkips(new Set());
    setValidateResult(null);
    setImportResult(null);
    setHeldValidationErrors([]);
  };

  const downloadErrorsList = (errors: ImportError[], filename: string) => {
    const rows = errorRowNumbers(errors);
    if (!rows.length) {
      toast.error('No error rows to download');
      return false;
    }
    const ok = downloadErrorRowsCsv({
      filename,
      headers,
      rawRows,
      errors,
    });
    if (ok) toast.success(`Downloaded ${rows.length} error row(s)`);
    else toast.error('No error rows to download');
    return ok;
  };

  const handleDownloadValidationErrors = () => {
    const errors = validateResult?.errors?.length
      ? validateResult.errors
      : heldValidationErrors;
    downloadErrorsList(errors, `${config.entityId}_import_errors.csv`);
  };

  const handleDownloadSkipped = () => {
    if (!skippedRowsForValidate.length) {
      toast.error('No skipped rows to download');
      return;
    }
    const ok = downloadSkippedRowsCsv({
      filename: `${config.entityId}_import_skipped.csv`,
      headers,
      rawRows,
      skipped: skippedRowsForValidate,
    });
    if (ok) toast.success(`Downloaded ${skippedRowsForValidate.length} skipped row(s)`);
    else toast.error('No skipped rows to download');
  };

  const handleDownloadImportErrors = () => {
    if (importResult?.errors?.length) {
      downloadErrorsList(importResult.errors, `${config.entityId}_import_failed_rows.csv`);
      return;
    }
    handleDownloadValidationErrors();
  };

  const handleFixAndReupload = () => {
    handleDownloadImportErrors();
    resetWizard();
    toast('Downloaded error rows — fix them and upload that file next.', { icon: '📄' });
  };

  const runValidate = async () => {
    try {
      setValidating(true);
      setValidateResult(null);
      setHeldValidationErrors([]);
      const formData = buildFormData(true);
      const res = await api.post(config.api.importRows, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const message = res.data.message as ValidateResult;
      setValidateResult(message);
      if (message.ready_to_import) {
        toast.success('Ready to import');
      } else if ((message.valid_rows || 0) > 0) {
        toast(`${message.valid_rows} valid rows — you can import those and download errors separately.`, {
          icon: '⚠️',
        });
      } else {
        toast.error(message.message || 'Validation found issues');
      }
    } catch (err: unknown) {
      console.error(err);
      const data = (err as { response?: { data?: { message?: string; _server_messages?: string } } })
        ?.response?.data;
      let msg = 'Validation failed';
      if (data?.message && typeof data.message === 'string') msg = data.message;
      toast.error(msg);
    } finally {
      setValidating(false);
    }
  };

  const runImport = async () => {
    try {
      setImporting(true);
      setImportResult(null);
      // Keep validation errors for download after a valid-only import.
      setHeldValidationErrors(validateResult?.errors?.length ? [...validateResult.errors] : []);
      // Skip validation-error rows so only valid records are imported.
      const formData = buildFormData(false, importSkipRowNumbers);
      const res = await api.post(config.api.importRows, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const message = res.data.message as ImportResult;
      setImportResult(message);
      if (message.imported && message.imported > 0) {
        onSuccess?.();
      }
      if (message.success) toast.success(message.message || 'Import complete');
      else toast.error(message.message || 'Import finished with errors');
    } catch (err: unknown) {
      console.error(err);
      toast.error('Import failed');
      setStep('validate');
    } finally {
      setImporting(false);
    }
  };

  const canGoNext = (): boolean => {
    if (step === 'file') return Boolean(file && rawRows.length >= 0 && headers.length);
    if (step === 'mapping') return requiredMapped;
    if (step === 'review') return previewRows.some((r) => r.status === 'ready');
    if (step === 'validate') {
      // Allow continue when there is at least one valid row — error rows are skipped on import.
      return Boolean(validateResult && (validateResult.valid_rows || 0) > 0);
    }
    return false;
  };

  const validateImportButtonLabel = (): string => {
    if (!validateResult) return t('import.importNow');
    const n = validateResult.valid_rows || 0;
    if ((validateResult.errors?.length || 0) > 0 && n > 0) {
      return t('import.importValidOnly', {
        count: n,
        defaultValue: `Import ${n} valid row${n === 1 ? '' : 's'}`,
      });
    }
    return t('import.importNow');
  };

  const goNext = async () => {
    if (step === 'file') {
      setStep('mapping');
      return;
    }
    if (step === 'mapping') {
      saveMapping(config, mapping);
      rebuildPreview(rawRows, mapping, manualSkips);
      setStep('review');
      return;
    }
    if (step === 'review') {
      setValidateResult(null);
      setStep('validate');
      return;
    }
    if (step === 'validate') {
      setStep('complete');
      await runImport();
    }
  };

  const goBack = () => {
    if (step === 'complete') {
      setStep('validate');
      return;
    }
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) setStep(STEP_ORDER[idx - 1]);
  };

  const handleCloseSuccess = () => {
    onSuccess?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative inline-block w-full max-w-4xl p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{importEntityTitle(t, config)}</h3>
              <p className="text-sm text-gray-500">{importEntityDescription(t, config)}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center justify-center mb-8 flex-wrap gap-y-2">
            {STEP_ORDER.map((s, idx) => (
              <div key={s} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold ${
                    step === s
                      ? 'bg-primary-600 text-white'
                      : stepIndex > idx
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                  title={stepLabel(s)}
                >
                  {stepIndex > idx ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                </div>
                <span
                  className={`ml-2 mr-2 text-xs font-medium hidden sm:inline ${
                    step === s ? 'text-primary-700' : 'text-gray-500'
                  }`}
                >
                  {stepLabel(s)}
                </span>
                {idx < STEP_ORDER.length - 1 && (
                  <div
                    className={`w-8 sm:w-12 h-1 mx-1 ${
                      stepIndex > idx ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 min-h-[280px]">
            {step === 'file' && (
              <FileStep
                config={config}
                fileName={fileName}
                headers={headers}
                dragging={dragging}
                downloading={downloading}
                uomLabels={uomLabels}
                onDownloadTemplate={handleDownloadTemplate}
                onFileSelected={handleFileSelected}
                onDragState={setDragging}
              />
            )}
            {step === 'mapping' && (
              <MappingStep
                config={config}
                headers={headers}
                mapping={mapping}
                uomLabels={uomLabels}
                onChange={handleMappingChange}
              />
            )}
            {step === 'review' && (
              <ReviewStep config={config} rows={previewRows} onToggleSkip={handleToggleSkip} />
            )}
            {step === 'validate' && (
              <ValidateStep
                loading={validating}
                result={validateResult}
                onValidate={runValidate}
                onDownloadErrors={handleDownloadValidationErrors}
                onDownloadSkipped={handleDownloadSkipped}
                validCount={validateResult?.valid_rows}
                skippedRows={skippedRowsForValidate}
              />
            )}
            {step === 'complete' && (
              <CompleteStep
                loading={importing}
                result={importResult}
                entityLabel={config.doctypeLabel.replace(/^POS\s+/i, '').toLowerCase() + 's'}
                closeLabel={t('import.viewEntities', {
                  label: config.doctypeLabel.replace(/^POS\s+/i, ''),
                  defaultValue: config.closeLabel || `View ${config.doctypeLabel.replace(/^POS\s+/i, '')}s`,
                })}
                errorRowCount={
                  importErrorRows.length || errorRowNumbers(heldValidationErrors).length
                }
                onImportAnother={resetWizard}
                onClose={handleCloseSuccess}
                onDownloadErrors={
                  importErrorRows.length || heldValidationErrors.length
                    ? handleDownloadImportErrors
                    : undefined
                }
                onFixAndReupload={
                  importErrorRows.length || heldValidationErrors.length
                    ? handleFixAndReupload
                    : undefined
                }
              />
            )}
          </div>

          {step !== 'complete' && (
            <div className="mt-6 flex justify-between border-t border-gray-100 pt-6">
              <button
                type="button"
                onClick={goBack}
                disabled={step === 'file' || validating || importing}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium disabled:opacity-50"
              >
                {t('import.back')}
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
                >
                  {t('import.cancel')}
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!canGoNext() || validating || importing}
                  className="btn-primary disabled:opacity-50"
                  title={
                    step === 'validate' && validationErrorRows.length
                      ? `Error rows will be skipped. Import ${validateResult?.valid_rows || 0} valid row(s).`
                      : undefined
                  }
                >
                  {step === 'validate' ? validateImportButtonLabel() : t('import.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
